import { computed, onMounted, reactive, ref } from '@nuxtjs/composition-api';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import type { StateInterface, UseUiStateInterface } from '~/composables/useUiState/useUiState';
import { Logger } from '~/helpers/logger';

const htmlRef = ref<HTMLElement | null>(null);

const state = reactive<StateInterface>({
  isCartSidebarOpen: false,
  isLoginModalOpen: false,
  isNewsletterModalOpen: false,
  isCategoryGridView: true,
  isFilterSidebarOpen: false,
  isMobileMenuOpen: false,
  isCollectionSidebarOpen: false,
  isSubscriptionModalVisible: false,
  transparencyModalSrc: '',
  isContentLayerOpen: false,
  contentLayerTitle: '',
  contentLayerContent: '',
  sidebarActionType: '',
  isBundleLayerVisible: false,
  isProductZoomOpened: false,
  isEngravingInfoOpen: false,
  isRedirectModalOpen: false,
  isSectionModalOpen: false,
  isSamplesProductsOpen: false,
  isSearchSidebarOpen: false,
  isNotifyMeModalOpen: false,
  notifyMeProductId: null
});

/**
 * Global store for managing UI state.
 *
 * See the {@link UseUiStateInterface} for a list of methods and values available in this composable.
 */
export function useUiState(): UseUiStateInterface {
  onMounted(() => {
    htmlRef.value = document.documentElement;
  });

  const toggleRedirectModal = () => {
    state.isRedirectModalOpen = !state.isRedirectModalOpen;
  };
  const toggleMobileMenu = () => {
    state.isMobileMenuOpen = !state.isMobileMenuOpen;
  };

  const toggleBundleLayer = () => {
    state.isBundleLayerVisible = !state.isBundleLayerVisible;
  };

  const toggleCartSidebar = (action_type: string | null) => {
    state.sidebarActionType = action_type;
    state.isCartSidebarOpen = !state.isCartSidebarOpen;
  };

  const toggleLoginModal = () => {
    if (state.isMobileMenuOpen) toggleMobileMenu();
    state.isLoginModalOpen = !state.isLoginModalOpen;
  };

  const toggleNewsletterModal = () => {
    state.isNewsletterModalOpen = !state.isNewsletterModalOpen;
  };

  const changeToCategoryGridView = () => {
    state.isCategoryGridView = true;
  };
  const changeToCategoryListView = () => {
    state.isCategoryGridView = false;
  };

  const toggleFilterSidebar = () => {
    state.isFilterSidebarOpen = !state.isFilterSidebarOpen;
  };
  const openSearchSidebar = () => {
    state.isSearchSidebarOpen = true;
  };
  const closeSearchSidebar = () => {
    state.isSearchSidebarOpen = false;
  };

  const toggleCollectionSidebar = () => {
    state.isCollectionSidebarOpen = !state.isCollectionSidebarOpen;
    htmlRef.value && (state.isCollectionSidebarOpen ? disableBodyScroll(htmlRef.value) : enableBodyScroll(htmlRef.value));
  };

  const hideCollectionSidebar = () => {
    if (state.isCollectionSidebarOpen) {
      toggleCollectionSidebar();
    }
  };

  const openEngravingInfo = () => {
    state.isEngravingInfoOpen = true;
    htmlRef.value && disableBodyScroll(htmlRef.value);
  };

  const hideEngravingInfo = () => {
    state.isEngravingInfoOpen = false;
    htmlRef.value && enableBodyScroll(htmlRef.value);
  };

  const toggleSubscriptionModal = () => {
    state.isSubscriptionModalVisible = !state.isSubscriptionModalVisible;
    htmlRef.value && (state.isSubscriptionModalVisible ? disableBodyScroll(htmlRef.value) : enableBodyScroll(htmlRef.value));
  };

  const setTransparencyModalSrc = (src: string) => {
    state.transparencyModalSrc = src;
  };

  const openContentLayer = (title: string, content: string) => {
    if (title && content) {
      state.contentLayerTitle = title;
      state.contentLayerContent = content;
      state.isContentLayerOpen = true;
    } else {
      Logger.error('Title and content are required for openContentLayer()');
    }
  };

  const closeContentLayer = () => {
    state.isContentLayerOpen = false;
  };

  const openProductZoom = () => {
    state.isProductZoomOpened = true;
  };

  const closeProductZoom = () => {
    state.isProductZoomOpened = false;
  };

  const toggleSectionModal = () => {
    state.isSectionModalOpen = !state.isSectionModalOpen;
  };

  const openSamplesProducts = () => {
    state.isSamplesProductsOpen = true;
    htmlRef.value && disableBodyScroll(htmlRef.value);
  };

  const hideSamplesProducts = () => {
    state.isSamplesProductsOpen = false;
    htmlRef.value && enableBodyScroll(htmlRef.value);
  };

  const openNotifyMeModal = (productId?: number) => {
    state.notifyMeProductId = productId ?? null;
    state.isNotifyMeModalOpen = true;
  };

  const toggleNotifyMeModal = () => {
    state.isNotifyMeModalOpen = !state.isNotifyMeModalOpen;
    if (!state.isNotifyMeModalOpen) {
      state.notifyMeProductId = null;
    }
  };

  return {
    isMobileMenuOpen: computed(() => state.isMobileMenuOpen),
    isCartSidebarOpen: computed(() => state.isCartSidebarOpen),
    isLoginModalOpen: computed(() => state.isLoginModalOpen),
    isNewsletterModalOpen: computed(() => state.isNewsletterModalOpen),
    isCategoryGridView: computed(() => state.isCategoryGridView),
    isFilterSidebarOpen: computed(() => state.isFilterSidebarOpen),
    isCollectionSidebarOpen: computed(() => state.isCollectionSidebarOpen),
    isEngravingInfoOpen: computed(() => state.isEngravingInfoOpen),
    isSubscriptionModalVisible: computed(() => state.isSubscriptionModalVisible),
    transparencyModalSrc: computed(() => state.transparencyModalSrc),
    sidebarActionType: computed(() => state.sidebarActionType),
    isBundleLayerVisible: computed(() => state.isBundleLayerVisible),
    isProductZoomOpened: computed(() => state.isProductZoomOpened),
    isRedirectModalOpen: computed(() => state.isRedirectModalOpen),
    isSectionModalOpen: computed(() => state.isSectionModalOpen),
    isSamplesProductsOpen: computed(() => state.isSamplesProductsOpen),
    isSearchSidebarOpen: computed(() => state.isSearchSidebarOpen),
    isNotifyMeModalOpen: computed(() => state.isNotifyMeModalOpen),
    toggleBundleLayer,
    toggleMobileMenu,
    toggleCollectionSidebar,
    toggleCartSidebar,
    toggleLoginModal,
    toggleNewsletterModal,
    changeToCategoryGridView,
    changeToCategoryListView,
    toggleFilterSidebar,
    toggleSubscriptionModal,
    hideCollectionSidebar,
    openContentLayer,
    closeContentLayer,
    setTransparencyModalSrc,
    openProductZoom,
    closeProductZoom,
    toggleRedirectModal,
    isContentLayerOpen: computed(() => state.isContentLayerOpen),
    contentLayerTitle: computed(() => state.contentLayerTitle),
    contentLayerContent: computed(() => state.contentLayerContent),
    openEngravingInfo,
    toggleSectionModal,
    hideEngravingInfo,
    openSamplesProducts,
    hideSamplesProducts,
    notifyMeProductId: computed(() => state.notifyMeProductId),
    openNotifyMeModal,
    toggleNotifyMeModal,
    openSearchSidebar,
    closeSearchSidebar
  };
}

export default useUiState;
export * from './useUiState';
